import { useState, useEffect } from "react";
import {
  BSModal,
  DeleteModal,
  Field,
  PageHeader,
  Loading,
  EmptyState,
  apiFetch,
} from "../../components/helpers";

const EMPTY_FORM = {
  slide_id: null,
  title: "",
  subtitle: "",
  badge: "",
  accent: "#1D9E75",
  image: "",
  sort_order: 1,
  status: "Active",
};

export default function ManageSlides() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const imageUrl = (img) => {
    if (!img) return "";
    let clean = img.replace(/\\/g, "/").trim();
    clean = clean.replace(/^omdentalclinic\//, "");
    if (clean.startsWith("http://") || clean.startsWith("https://")) {
      return clean;
    }
    const baseUrl = process.env.REACT_APP_WEB_URL || "http://localhost/omdentalclinic";
    return `${baseUrl}/${clean.replace(/^\/+/, "")}`;
  };

  const load = async () => {
    setLoading(true);
    try {
      const json = await apiFetch(process.env.REACT_APP_SLIDE_VIEW);
      setRows(json.status ? json.data || [] : []);
    } catch {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setForm(row);
    setImageFile(null);
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async () => {
  if (!form.title.trim()) return setError("Title required");

  setSubmitting(true);
  setError("");

  try {
    const fd = new FormData();

    Object.keys(form).forEach((k) => {
      fd.append(k, form[k] ?? "");
    });

    if (imageFile) {
      fd.append("image_file", imageFile);
    }

    const url = form.slide_id
      ? process.env.REACT_APP_SLIDE_UPDATE
      : process.env.REACT_APP_SLIDE_INSERT;

    const json = await apiFetch(url, {
      method: "POST",
      body: fd,
    });

    console.log(json);

    if (json.status) {
      setModalOpen(false);
      load();
    } else {
      setError(json.message || "Failed");
    }
  } catch (err) {
    console.error(err);
    setError("Network Error");
  }

  setSubmitting(false);
};

  const handleDelete = async () => {
    try {
      const json = await apiFetch(process.env.REACT_APP_SLIDE_DELETE, {
        method: "POST",
        body: { slide_id: deleteId },
      });
      if (json.status) {
        setDeleteId(null);
        load();
      }
    } catch {}
  };

  return (
    <div className="p-3 p-lg-4">
      <PageHeader title="Manage Slides" subtitle={`${rows.length} slide(s)`}>
        <button className="btn text-white fw-bold" style={{ background: "#1D9E75", borderRadius: 10 }} onClick={openAdd}>
          + Add Slide
        </button>
      </PageHeader>

      <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
        <div className="card-body p-0">
          {loading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyState message="No slides found" />
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th className="px-4 py-3">Image</th>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Badge</th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.slide_id}>
                      <td className="px-4"><img src={imageUrl(r.image)} alt="" width="70" height="45" style={{objectFit:"cover",borderRadius:8}} /></td>
                      <td className="px-4">{r.title}</td>
                      <td className="px-4">{r.badge}</td>
                      <td className="px-4">{r.sort_order}</td>
                      <td className="px-4"><span className={`badge ${r.status === "Active" ? "text-bg-success" : "text-bg-secondary"}`}>{r.status}</span></td>
                      <td className="px-4">
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-info" onClick={() => openEdit(r)}>Edit</button>
                          <button className="btn btn-sm btn-danger" onClick={() => setDeleteId(r.slide_id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <BSModal open={modalOpen} title={form.slide_id ? "Edit Slide" : "Add Slide"} onClose={() => setModalOpen(false)}>
        <div className="row g-3">
          {[["Title", "title"],["Subtitle", "subtitle"],["Badge", "badge"],["Accent", "accent"],["Sort Order", "sort_order"]].map(([label, key]) => (
            <div className="col-md-6" key={key}>
              <Field label={label}>
                <input className="form-control" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
              </Field>
            </div>
          ))}

          <div className="col-md-6">
            <Field label="Image">
              <input type="file" className="form-control" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
            </Field>
          </div>

          {form.image && !imageFile && (
            <div className="col-md-6">
              <Field label="Current Image">
                <img src={imageUrl(form.image)} alt="" width="120" style={{borderRadius:8}} />
              </Field>
            </div>
          )}

          <div className="col-12">
            <Field label="Status">
              <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </Field>
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3">{error}</div>}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button className="btn btn-light" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn text-white" style={{ background: "#1D9E75" }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </BSModal>

      <DeleteModal open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} />
    </div>
  );
}